import React from 'react';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import {
  Badge,
  BadgePriority,
  Text,
  TextButton,
  TextButtonPriority,
} from 'wix-ui-tpa/cssVars';
import ChevronDown from 'wix-ui-icons-common/on-stage/ChevronDownSmall';
import ChevronUp from 'wix-ui-icons-common/on-stage/ChevronUpSmall';
import { classes, st } from './ParticipantSection.st.css';
import { ParticipantSectionDataHooks } from './dataHooks';
import { getParticipantLabel } from '../../../../utils/multiParticipantFlow';

export interface ParticipantSectionProps {
  participantIndex: number;
  totalParticipants: number;
  isExpanded: boolean;
  onToggle: () => void;
  onRemove?: () => void;
  // Participant's name once the form has one; the badge alone identifies them otherwise.
  title?: string;
  children: React.ReactNode;
}

/**
 * One participant's slice of the booking form (form, price variant, add-ons, ...).
 *
 * With a single participant it renders its children as-is, so the form looks exactly like
 * today's. With several participants each section gets a header ("Participant N", name,
 * Remove) and collapses like an accordion. The body stays mounted while collapsed so its
 * form-viewer ref remains available for validation.
 */
const ParticipantSection = React.forwardRef<
  HTMLDivElement,
  ParticipantSectionProps
>(
  (
    {
      participantIndex,
      totalParticipants,
      isExpanded,
      onToggle,
      onRemove,
      title,
      children,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const { isMobile, isRTL } = useEnvironment();
    const showHeader = totalParticipants > 1;
    const bodyId = `participant-section-body-${participantIndex}`;
    const ToggleIcon = isExpanded ? ChevronUp : ChevronDown;

    return (
      <div
        ref={ref}
        className={st(classes.root, {
          isMobile,
          isRTL,
          withHeader: showHeader,
        })}
        data-hook={`${ParticipantSectionDataHooks.ROOT}-${participantIndex}`}
      >
        {showHeader && (
          <div
            className={classes.header}
            data-hook={ParticipantSectionDataHooks.HEADER}
          >
            <button
              type="button"
              className={classes.toggle}
              data-hook={ParticipantSectionDataHooks.TOGGLE}
              onClick={onToggle}
              aria-expanded={isExpanded}
              aria-controls={bodyId}
            >
              <Badge
                priority={BadgePriority.default}
                className={classes.badge}
                data-hook={ParticipantSectionDataHooks.BADGE}
              >
                {getParticipantLabel(t, participantIndex + 1)}
              </Badge>
              {title && (
                <Text
                  className={classes.title}
                  data-hook={ParticipantSectionDataHooks.TITLE}
                >
                  {title}
                </Text>
              )}
              <span className={classes.chevron} aria-hidden>
                <ToggleIcon />
              </span>
            </button>
            {onRemove && (
              <TextButton
                className={classes.remove}
                priority={TextButtonPriority.secondary}
                data-hook={ParticipantSectionDataHooks.REMOVE}
                onClick={onRemove}
              >
                {t('bookings.form.collectParticipant.removeParticipant.label')}
              </TextButton>
            )}
          </div>
        )}
        <div
          id={bodyId}
          className={classes.body}
          data-hook={ParticipantSectionDataHooks.BODY}
          hidden={showHeader && !isExpanded}
        >
          {children}
        </div>
      </div>
    );
  },
);

ParticipantSection.displayName = 'ParticipantSection';

export default ParticipantSection;
